import { useState } from "react";
import { useNavigate } from "react-router";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { useCreditNotes } from "../hooks/useInvoicing";
import type { CreditNoteSummary } from "../services/invoicingApi";

const formatDate = (value?: string) => {
  if (!value) return "—";
  return new Date(value).toLocaleDateString("en-KE", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
};

const formatMoney = (value?: number) =>
  new Intl.NumberFormat("en-KE", {
    style: "currency",
    currency: "KES",
    maximumFractionDigits: 2,
  }).format(value ?? 0);

export const CreditNotesPage = () => {
  const navigate = useNavigate();
  const creditNotesQuery = useCreditNotes();

  const [search, setSearch] = useState("");
  const [selectedNote, setSelectedNote] = useState<CreditNoteSummary | null>(null);

  const rows = (creditNotesQuery.data ?? []).filter((cn) => {
    if (!search.trim()) return true;
    const term = search.trim().toLowerCase();
    return (
      cn.creditNoteNumber.toLowerCase().includes(term) ||
      cn.invoiceNumber.toLowerCase().includes(term) ||
      cn.customerName.toLowerCase().includes(term)
    );
  });

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold tracking-tight">Credit Notes</h1>
        <p className="mt-1 text-muted-foreground">
          All credit notes auto-generated when void requests were approved. These reverse revenue
          and VAT on the original invoices.
        </p>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Credit Notes</CardTitle>
          <CardDescription>
            Each credit note mirrors a voided invoice's line items and financials.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="max-w-xs space-y-2">
            <Label htmlFor="cn-search">Search</Label>
            <Input
              id="cn-search"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Credit note #, invoice #, or customer"
            />
          </div>

          {creditNotesQuery.isLoading ? (
            <p className="text-sm text-muted-foreground">Loading credit notes...</p>
          ) : rows.length === 0 ? (
            <p className="text-sm text-muted-foreground py-8 text-center">
              No credit notes found.
            </p>
          ) : (
            <div className="overflow-x-auto rounded-lg border">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Credit Note #</TableHead>
                    <TableHead>Invoice #</TableHead>
                    <TableHead>Customer</TableHead>
                    <TableHead>Issued Date</TableHead>
                    <TableHead>Amount Reversed</TableHead>
                    <TableHead>Reason</TableHead>
                    <TableHead>Issued By</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {rows.map((cn) => (
                    <TableRow key={cn.id}>
                      <TableCell className="font-mono text-xs">{cn.creditNoteNumber}</TableCell>
                      <TableCell
                        className="font-mono text-xs cursor-pointer hover:underline text-blue-600"
                        onClick={() => navigate(`/invoicing/ar/${cn.invoiceId}`)}
                      >
                        {cn.invoiceNumber}
                      </TableCell>
                      <TableCell>{cn.customerName}</TableCell>
                      <TableCell>{formatDate(cn.issuedDate)}</TableCell>
                      <TableCell className="text-red-600 font-medium">
                        -{formatMoney(cn.totalAmount)}
                      </TableCell>
                      <TableCell className="max-w-xs truncate text-sm text-muted-foreground">
                        {cn.reason}
                      </TableCell>
                      <TableCell>{cn.issuedByUserName}</TableCell>
                      <TableCell className="text-right">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => setSelectedNote(cn)}
                        >
                          View
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Credit note detail dialog */}
      <Dialog open={!!selectedNote} onOpenChange={(open) => { if (!open) setSelectedNote(null); }}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>{selectedNote?.creditNoteNumber}</DialogTitle>
          </DialogHeader>
          {selectedNote && (
            <div className="space-y-4 text-sm">
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <p className="text-muted-foreground">Invoice reversed</p>
                  <p
                    className="font-mono font-medium cursor-pointer hover:underline text-blue-600"
                    onClick={() => {
                      navigate(`/invoicing/ar/${selectedNote.invoiceId}`);
                      setSelectedNote(null);
                    }}
                  >
                    {selectedNote.invoiceNumber}
                  </p>
                </div>
                <div>
                  <p className="text-muted-foreground">Customer</p>
                  <p className="font-medium">{selectedNote.customerName}</p>
                </div>
                <div>
                  <p className="text-muted-foreground">Issued date</p>
                  <p>{formatDate(selectedNote.issuedDate)}</p>
                </div>
                <div>
                  <p className="text-muted-foreground">Issued by</p>
                  <p>{selectedNote.issuedByUserName}</p>
                </div>
                <div className="col-span-2">
                  <p className="text-muted-foreground">Amount reversed</p>
                  <p className="text-red-600 text-lg font-semibold">
                    -{formatMoney(selectedNote.totalAmount)}
                  </p>
                </div>
                <div className="col-span-2">
                  <p className="text-muted-foreground">Reason</p>
                  <p>{selectedNote.reason}</p>
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};
